import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Switch,
  InputNumber,
  notification,
  Space,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";

import ImgUploader from "../../../../components/ImageUploader/ImgUploader";
import { ASSETS_BASE_URL } from "../../../../config/constants";
import BigPrizeTimeSlotsField from "./BigPrizeTimeSlotsField";
import GameSettingsSummary from "./GameSettingsSummary";

const { Option } = Select;

function ImageUploadField({ form, name }) {
  const [openUpload, setOpenUpload] = useState(false);
  const value = Form.useWatch(name, form);

  return (
    <>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <Button type="primary" onClick={() => setOpenUpload(true)}>
          Select image
        </Button>
        {value ? (
          <Button
            type="link"
            danger
            onClick={() => form.setFieldsValue({ [name]: "" })}
          >
            Remove
          </Button>
        ) : null}
      </div>
      {value ? (
        <div className="mt-2">
          <img
            src={`${ASSETS_BASE_URL}${value}`}
            alt="Game"
            style={{ maxWidth: 160, maxHeight: 120, objectFit: "contain" }}
          />
        </div>
      ) : null}
      <ImgUploader
        openUpload={openUpload}
        toggleUploadOpen={() => setOpenUpload(false)}
        setImgUrl={(url) => form.setFieldsValue({ [name]: url || "" })}
      />
    </>
  );
}

function NumberListField({ field }) {
  const itemLabel = field.itemLabel || "Random Prize Number";
  const help = field.help || "Display-only amount for non-match board slots.";

  return (
    <Form.List name={field.name}>
      {(fields, { add, remove }) => (
        <div style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <h6 style={{ margin: 0, color: "#6f42c1" }}>
              # {field.label || "Unmatched Random Prize Number"}
            </h6>
            <Button
              type="primary"
              style={{ background: "#28a745", borderColor: "#28a745" }}
              icon={<PlusOutlined />}
              onClick={() => add()}
            >
              Add Number
            </Button>
          </div>
          <Row gutter={[16, 8]}>
            {fields.map(({ key, name, ...restField }) => (
              <Col span={8} key={key}>
                <Form.Item
                  label={`${itemLabel} *`}
                  required
                  extra={<span style={{ color: "#888" }}>{help}</span>}
                >
                  <Space.Compact style={{ width: "100%" }}>
                    <Form.Item
                      {...restField}
                      name={name}
                      noStyle
                      rules={[
                        { required: true, message: `${itemLabel} is required.` },
                        {
                          validator: (_, value) => {
                            if (value == null || value === "") {
                              return Promise.reject(new Error(`${itemLabel} is required.`));
                            }
                            if (Number(value) < 0) {
                              return Promise.reject(new Error("Must be 0 or greater."));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: "100%" }}
                        min={0}
                        placeholder="e.g. 5000"
                      />
                    </Form.Item>
                    <Button
                      danger
                      type="primary"
                      icon={<CloseOutlined />}
                      onClick={() => remove(name)}
                    />
                  </Space.Compact>
                </Form.Item>
              </Col>
            ))}
          </Row>
          {!fields.length ? (
            <p style={{ color: "#888", marginBottom: 0 }}>
              No numbers yet. Click “Add Number” to add display-only prizes for
              non-match board slots.
            </p>
          ) : null}
        </div>
      )}
    </Form.List>
  );
}

function renderField(field, form) {
  const commonProps = field.props || {};

  if (field.type === "image") {
    return <ImageUploadField form={form} name={field.name} />;
  }

  if (field.type === "select") {
    return (
      <Select {...commonProps}>
        {(field.options || []).map((option) => (
          <Option key={String(option.value)} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
    );
  }

  if (field.type === "multiselect") {
    return (
      <Select
        mode="multiple"
        options={field.options || []}
        optionFilterProp="label"
        {...commonProps}
      />
    );
  }

  if (field.type === "number") {
    return <InputNumber style={{ width: "100%" }} {...commonProps} />;
  }

  if (field.type === "switch") {
    return <Switch {...commonProps} />;
  }

  if (field.type === "textarea") {
    return <Input.TextArea rows={4} {...commonProps} />;
  }

  return <Input {...commonProps} />;
}

export default function SmartRtpFormPage({
  title,
  backTo,
  initialValues,
  fields,
  onSubmit,
  showGameSummary = false,
}) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const watchedGameId = Form.useWatch("game_id", form);
  const watchedSettingType = Form.useWatch("setting_type", form);
  const visibleFields = (fields || []).filter(
    (field) => !field.gameOnly || watchedSettingType === "Game"
  );
  const summaryGameId =
    showGameSummary &&
    (watchedSettingType == null || watchedSettingType === "Game") &&
    watchedGameId
      ? watchedGameId
      : null;

  useEffect(() => {
    form.setFieldsValue(initialValues || {});
    document.title = `Crown King || ${title}`;
  }, [form, initialValues, title]);

  const handleSubmit = async (values) => {
    const res = await onSubmit(values);
    if (res?.status === true) {
      notification.success({
        message: "Success",
        description: "Data successfully added/updated.",
        icon: <CheckCircleOutlined style={{ color: "green" }} />,
        duration: 2,
      });
      navigate(backTo);
      return;
    }

    notification.error({
      message: "Error",
      description: res?.message || "Unable to process request.",
      icon: <InfoCircleOutlined style={{ color: "red" }} />,
      duration: 2,
    });
  };

  return (
    <div className="container-fluid">
      <div className="Inventory_edit_page_main_div">
        <h5 className="inventory_edit_page_heading">{title}</h5>
        <Link to={backTo}>
          <Button type="secondary">
            <ArrowLeftOutlined /> Back
          </Button>
        </Link>
      </div>

      {summaryGameId ? <GameSettingsSummary gameId={summaryGameId} /> : null}

      <div className="card shadow mb-4 dataBase_table_Add_Data">
        <div style={{ padding: "20px" }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={initialValues}
          >
            <Row gutter={16}>
              {visibleFields.map((field) =>
                field.type === "numberList" ? (
                  <Col span={24} key={field.name} style={{ marginTop: 8 }}>
                    <NumberListField field={field} />
                  </Col>
                ) : field.type === "bigPrizeTimeSlots" ? (
                  <Col span={24} key={field.name} style={{ marginTop: 16 }}>
                    <BigPrizeTimeSlotsField field={field} gameId={watchedGameId} />
                  </Col>
                ) : (
                  <Col span={field.span || 8} key={field.name}>
                    <Form.Item
                      label={field.label}
                      name={field.name}
                      valuePropName={field.type === "switch" ? "checked" : "value"}
                      rules={field.rules || []}
                      style={field.formItemStyle}
                    >
                      {renderField(field, form)}
                    </Form.Item>
                  </Col>
                )
              )}
            </Row>
            <div className="mt-3">
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}
